import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import PublicLayout from "@/components/public/PublicLayout";
import MilitaryVerifyCard from "@/components/public/MilitaryVerifyCard";
import { publicApi, fileUrl } from "@/lib/api";
import { money } from "@/lib/format";
import { useCart } from "@/context/CartContext";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Minus, Plus, Trash2, ShoppingBag, Lock, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const MAX_POLLS = 8;
const POLL_INTERVAL_MS = 2000;

export default function Checkout() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { items, updateQty, removeItem, subtotal, count, clear } = useCart();
  const { customer } = useCustomerAuth();
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "" });
  // phase: 'cart' | 'redirecting' | 'verifying' | 'paid' | 'failed'
  const [phase, setPhase] = useState("cart");
  const [paidOrderId, setPaidOrderId] = useState(null);
  const [errMsg, setErrMsg] = useState("");
  const pollStarted = useRef(false);

  useEffect(() => {
    if (customer) {
      setForm((f) => ({ ...f, name: customer.name || f.name, email: customer.email || f.email, phone: customer.phone || f.phone }));
    }
  }, [customer]);

  const pollStatus = useCallback(
    async (sessionId, attempt) => {
      if (attempt >= MAX_POLLS) {
        setErrMsg("We couldn't confirm your payment yet. If you were charged, your order is saved and our team will follow up.");
        setPhase("failed");
        return;
      }
      try {
        const { data } = await publicApi.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          clear();
          setPaidOrderId(data.order_id);
          setPhase("paid");
          return;
        }
        if (data.status === "expired") {
          setErrMsg("This payment session expired before it was completed. Please try again.");
          setPhase("failed");
          return;
        }
      } catch (e) {
        // transient — keep polling
      }
      setTimeout(() => pollStatus(sessionId, attempt + 1), POLL_INTERVAL_MS);
    },
    [clear]
  );

  // On return from Stripe, the URL carries ?session_id=...
  useEffect(() => {
    const sid = searchParams.get("session_id");
    if (sid && !pollStarted.current) {
      pollStarted.current = true;
      setPhase("verifying");
      pollStatus(sid, 0);
    }
  }, [searchParams, pollStatus]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const pay = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please add your name and email so we can confirm your order.");
      return;
    }
    setPhase("redirecting");
    try {
      const { data } = await publicApi.post("/checkout/session", {
        items: items.map((it) => ({ id: it.id, qty: it.qty })),
        contact: { name: form.name, email: form.email, phone: form.phone, address: form.address },
        origin_url: window.location.origin,
      });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error("No checkout URL received");
      }
    } catch (e) {
      setPhase("cart");
      toast.error(e?.response?.data?.detail || "We couldn't start secure checkout. Please try again.");
    }
  };

  // ---------- Verifying payment ----------
  if (phase === "verifying") {
    return (
      <PublicLayout>
        <section className="max-w-2xl mx-auto px-5 py-24 text-center" data-testid="checkout-verifying">
          <Loader2 size={40} className="mx-auto mb-5 text-[#c9a86a] animate-spin" />
          <h1 className="text-2xl md:text-3xl font-semibold text-[var(--text)] mb-3">Confirming your payment</h1>
          <p className="text-[var(--text-2)]">Please hold on a moment — we're securely verifying your transaction with Stripe.</p>
        </section>
      </PublicLayout>
    );
  }

  // ---------- Payment confirmed ----------
  if (phase === "paid") {
    return (
      <PublicLayout>
        <section className="max-w-2xl mx-auto px-5 py-24 text-center" data-testid="checkout-success">
          <CheckCircle2 size={48} className="mx-auto mb-5 text-[#c9a86a]" />
          <h1 className="text-2xl md:text-3xl font-semibold text-[var(--text)] mb-3">Thank you — your order is confirmed</h1>
          <p className="text-[var(--text-2)] mb-2">A confirmation has been logged to your account and our concierge team.</p>
          {paidOrderId && (
            <p className="text-[var(--muted)] text-sm mb-8">
              Order reference: <span className="text-[#c9a86a] font-mono" data-testid="success-order-id">{paidOrderId.slice(0, 8).toUpperCase()}</span>
            </p>
          )}
          <div className="flex items-center justify-center gap-3">
            <Button onClick={() => navigate("/shop")} data-testid="success-continue-btn" className="bg-[#c9a86a] hover:bg-[#b89455] text-[var(--on-gold)] font-semibold rounded-none">
              Continue Shopping
            </Button>
            <Button onClick={() => navigate("/account")} variant="outline" data-testid="success-account-btn" className="border-[var(--border-2)] text-[var(--text)] hover:bg-[var(--panel-2)] rounded-none">
              View My Orders
            </Button>
          </div>
        </section>
      </PublicLayout>
    );
  }

  // ---------- Payment failed / expired / timeout ----------
  if (phase === "failed") {
    return (
      <PublicLayout>
        <section className="max-w-2xl mx-auto px-5 py-24 text-center" data-testid="checkout-failed">
          <AlertCircle size={48} className="mx-auto mb-5 text-[#c97a6a]" />
          <h1 className="text-2xl md:text-3xl font-semibold text-[var(--text)] mb-3">Payment not completed</h1>
          <p className="text-[var(--text-2)] mb-8 max-w-md mx-auto">{errMsg}</p>
          <div className="flex items-center justify-center gap-3">
            <Button
              onClick={() => { pollStarted.current = false; setPhase("cart"); navigate("/checkout", { replace: true }); }}
              data-testid="failed-retry-btn"
              className="bg-[#c9a86a] hover:bg-[#b89455] text-[var(--on-gold)] font-semibold rounded-none"
            >
              Back to Checkout
            </Button>
            <Button onClick={() => navigate("/shop")} variant="outline" data-testid="failed-shop-btn" className="border-[var(--border-2)] text-[var(--text)] hover:bg-[var(--panel-2)] rounded-none">
              Browse the Shop
            </Button>
          </div>
        </section>
      </PublicLayout>
    );
  }

  // ---------- Cart / checkout form ----------
  const redirecting = phase === "redirecting";
  const verified = !!customer?.is_military_verified;
  const discountAmount = verified ? Math.round(subtotal * 0.10 * 100) / 100 : 0;
  const total = Math.round((subtotal - discountAmount) * 100) / 100;
  return (
    <PublicLayout>
      <section className="max-w-6xl mx-auto px-5 py-12">
        <h1 className="text-2xl md:text-3xl font-semibold text-[var(--text)] mb-8">Checkout</h1>

        {count === 0 ? (
          <div className="text-center text-[var(--muted)] py-20" data-testid="checkout-empty">
            <ShoppingBag size={32} className="mx-auto mb-3 opacity-40" />
            <p className="mb-4">Your cart is empty.</p>
            <Link to="/shop" className="text-[#c9a86a] hover:text-[#b89455] transition-colors">Browse the shop</Link>
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Line items + contact */}
            <div className="lg:col-span-2 space-y-6">
              <Card className="bg-[var(--panel)] border-[var(--border-lch)] p-5">
                <h2 className="text-[var(--text)] font-medium mb-4">Your Items ({count})</h2>
                <div className="space-y-4">
                  {items.map((it) => (
                    <div key={it.id} className="flex gap-4 border-b border-[var(--border-lch)] pb-4 last:border-0 last:pb-0" data-testid={`checkout-item-${it.id}`}>
                      <div className="h-20 w-20 rounded bg-[var(--panel-2)] overflow-hidden shrink-0">
                        {it.image && <img src={fileUrl(it.image)} alt={it.name} className="h-full w-full object-cover" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[var(--text)]">{it.name}</div>
                        <div className="text-[#c9a86a] text-sm mt-0.5">{money(it.price)}</div>
                        <div className="flex items-center gap-2 mt-2">
                          <button onClick={() => updateQty(it.id, it.qty - 1)} className="h-7 w-7 flex items-center justify-center border border-[var(--border-2)] rounded text-[var(--text-2)] hover:text-[var(--text)]"><Minus size={12} /></button>
                          <span className="text-sm w-6 text-center text-[var(--text)]">{it.qty}</span>
                          <button onClick={() => updateQty(it.id, it.qty + 1)} className="h-7 w-7 flex items-center justify-center border border-[var(--border-2)] rounded text-[var(--text-2)] hover:text-[var(--text)]"><Plus size={12} /></button>
                          <button onClick={() => removeItem(it.id)} className="ml-auto text-[var(--muted)] hover:text-red-400"><Trash2 size={14} /></button>
                        </div>
                      </div>
                      <div className="text-[var(--text)] text-sm">{money(it.price * it.qty)}</div>
                    </div>
                  ))}
                </div>
              </Card>

              <Card className="bg-[var(--panel)] border-[var(--border-lch)] p-5">
                <h2 className="text-[var(--text)] font-medium mb-4">Contact &amp; Shipping</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5"><Label className="text-xs text-[var(--text-2)]">Full Name</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} className="bg-[var(--bg)] border-[var(--border-2)] text-[var(--text)]" data-testid="checkout-name" /></div>
                  <div className="space-y-1.5"><Label className="text-xs text-[var(--text-2)]">Email</Label><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="bg-[var(--bg)] border-[var(--border-2)] text-[var(--text)]" data-testid="checkout-email" /></div>
                  <div className="space-y-1.5"><Label className="text-xs text-[var(--text-2)]">Phone</Label><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="bg-[var(--bg)] border-[var(--border-2)] text-[var(--text)]" data-testid="checkout-phone" /></div>
                  <div className="space-y-1.5 sm:col-span-2"><Label className="text-xs text-[var(--text-2)]">Shipping Address</Label><Textarea rows={2} value={form.address} onChange={(e) => set("address", e.target.value)} className="bg-[var(--bg)] border-[var(--border-2)] text-[var(--text)]" data-testid="checkout-address" /></div>
                </div>
              </Card>

              <MilitaryVerifyCard />
            </div>

            {/* Summary */}
            <div>
              <Card className="bg-[var(--panel)] border-[var(--border-lch)] p-5 sticky top-24">
                <h2 className="text-[var(--text)] font-medium mb-4">Order Summary</h2>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between text-[var(--text-2)]"><span>Subtotal</span><span className="text-[var(--text)]" data-testid="summary-subtotal">{money(subtotal)}</span></div>
                  {verified && (
                    <div className="flex justify-between text-[#c9a86a]" data-testid="summary-discount">
                      <span>Military &amp; Veteran (10%)</span><span>−{money(discountAmount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-[var(--text-2)]"><span>Shipping</span><span className="text-[var(--muted)]">Calculated at payment</span></div>
                </div>
                <div className="border-t border-[var(--border-lch)] mt-4 pt-4 flex justify-between">
                  <span className="text-[var(--text)] font-medium">Total</span>
                  <span className="text-[#c9a86a] font-medium" data-testid="summary-total">{money(total)}</span>
                </div>
                <Button onClick={pay} disabled={redirecting} data-testid="checkout-pay-btn" className="w-full mt-5 bg-[#c9a86a] hover:bg-[#b89455] text-[var(--on-gold)] font-semibold rounded-none disabled:opacity-70">
                  {redirecting ? (<><Loader2 size={14} className="mr-2 animate-spin" /> Redirecting to Stripe…</>) : (<><Lock size={14} className="mr-2" /> Proceed to Payment</>)}
                </Button>
                <p className="text-[11px] text-[var(--muted)] mt-3 text-center">Secured by Stripe. You'll be redirected to complete payment.</p>
              </Card>
            </div>
          </div>
        )}
      </section>
    </PublicLayout>
  );
}
